import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import {
  BANNER_WIDTH,
  BANNER_HEIGHT,
  BANNER_RATIO,
  BANNER_SIZE_LABEL,
  validateBannerDimensions,
} from "./bannerImage";

export default function BannerCropDialog({ src, onCancel, onSave }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false),
    [saving, setSaving] = useState(false),
    [error, setError] = useState("");
  const save = () => {
    const cropper = ref.current?.cropper;
    if (!cropper || !ready || saving) return;
    const crop = cropper.getData();
    const issue = validateBannerDimensions({
      width: Math.round(crop.width),
      height: Math.round(crop.height),
    });
    if (issue) {
      setError("This crop is too small. Zoom out or use a larger image.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const canvas = cropper.getCroppedCanvas({
        width: BANNER_WIDTH,
        height: BANNER_HEIGHT,
        fillColor: "#fff",
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });
      if (!canvas || validateBannerDimensions(canvas, true))
        throw new Error("Unable to create the banner crop. Please try again.");
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            setError("Unable to create the banner file. Please try again.");
            setSaving(false);
            return;
          }
          setSaving(false);
          onSave(
            new File([blob], "mobile-banner-1200x600.jpg", {
              type: "image/jpeg",
            }),
          );
        },
        "image/jpeg",
        0.9,
      );
    } catch (err) {
      setError(err.message || "Unable to crop this image. Please try again.");
      setSaving(false);
    }
  };
  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={saving ? undefined : onCancel}
      aria-labelledby="banner-crop-title"
    >
      <DialogTitle id="banner-crop-title">Crop mobile slider banner</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag the image to frame your banner. The 2:1 shape is locked; the saved image will be{" "}
          {BANNER_SIZE_LABEL}. Keep important text away from the edges.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Cropper
          ref={ref}
          src={src}
          style={{ width: "100%", height: 360 }}
          aspectRatio={BANNER_RATIO}
          initialAspectRatio={BANNER_RATIO}
          viewMode={1}
          dragMode="move"
          autoCropArea={1}
          cropBoxResizable
          cropBoxMovable
          guides
          background={false}
          responsive
          checkOrientation
          ready={() => setReady(true)}
          error={() => setError("Unable to load the image for cropping. Choose another image.")}
        />
        <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
          <Button disabled={!ready || saving} onClick={() => ref.current?.cropper.zoom(-0.1)}>
            Zoom out
          </Button>
          <Button disabled={!ready || saving} onClick={() => ref.current?.cropper.zoom(0.1)}>
            Zoom in
          </Button>
          <Button disabled={!ready || saving} onClick={() => ref.current?.cropper.reset()}>
            Reset crop
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
        <LoadingButton loading={saving} disabled={!ready} variant="contained" onClick={save}>
          Use banner crop
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
